import { RefObject, useEffect, useState } from 'react';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';

import { PAGE_HEADER_STICKY_HEIGHT } from '@/components/page/PageHeader/StickyPageHeader';

const getSidebarVisibleHeight = (
  isStickyHeader: boolean,
  sidebarRef: RefObject<HTMLDivElement>,
  pageHeaderRef: RefObject<HTMLDivElement>,
) => {
  if (!sidebarRef.current) {
    return 0;
  }
  const rect = sidebarRef.current.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;

  const visibleTop = isStickyHeader ? 0 : Math.max(rect.top, 0);
  const visibleBottom = Math.min(rect.bottom, windowHeight);

  const stickyHeaderHeight = isStickyHeader ? PAGE_HEADER_STICKY_HEIGHT + (pageHeaderRef.current?.offsetTop || 0) : 0;

  const visibleHeight = Math.max(0, visibleBottom - visibleTop - stickyHeaderHeight);

  return visibleHeight;
};

export const useIsFullSidebarVisibleRefresh = create(
  combine({ state: 0 }, (set, get) => ({ refresh: () => set({ state: (get().state + 1) % 10 }) })),
);

const useIsFullSidebarVisible = ({
  isStickyHeader,
  platformShellHeaderHeight,
  footerRef,
  headerRef,
  sidebarRef,
  pageHeaderRef,
  titleRef,
}: {
  isStickyHeader: boolean;
  platformShellHeaderHeight: number;
  footerRef: RefObject<HTMLDivElement>;
  headerRef: RefObject<HTMLDivElement>;
  sidebarRef: RefObject<HTMLDivElement>;
  pageHeaderRef: RefObject<HTMLDivElement>;
  titleRef: RefObject<HTMLDivElement>;
}) => {
  const [isFullSidebarVisible, setIsFullSidebarVisible] = useState(true);
  const { state } = useIsFullSidebarVisibleRefresh();

  useEffect(() => {
    const checkSidebarHeight = () => {
      if (!sidebarRef.current || !headerRef.current || !footerRef.current) {
        return;
      }

      // The sub view's heading sits above `Body`, so it is not part of the header element measured
      // here -- without it a config page's sidebar is called fully visible while the heading pushes
      // it past the viewport. Zero on the pages that have no heading.
      const titleHeight = titleRef.current?.offsetHeight ?? 0;

      setIsFullSidebarVisible(
        getSidebarVisibleHeight(isStickyHeader, sidebarRef, pageHeaderRef) >
          headerRef.current.offsetHeight + footerRef.current.offsetHeight + titleHeight,
      );
    };

    checkSidebarHeight();

    window.addEventListener('resize', checkSidebarHeight);
    if (!isStickyHeader) {
      window.addEventListener('scroll', checkSidebarHeight);
    }

    return () => {
      window.removeEventListener('resize', checkSidebarHeight);
      window.removeEventListener('scroll', checkSidebarHeight);
    };
  }, [footerRef, state, headerRef, isStickyHeader, platformShellHeaderHeight, sidebarRef, pageHeaderRef, titleRef]);

  return {
    isFullSidebarVisible,
    footerRef,
    headerRef,
    sidebarRef,
  };
};

export default useIsFullSidebarVisible;
